import type { ReactNode } from "react";

import { AxiosError } from "axios";

import { isValidHTTPURL } from "src/connections/urls";
import type { JSONValue } from "src/datasets/DatasetTable/types";

export const pluralize = (count: number, noun: string, suffix = "s") =>
  `${count} ${noun}${count !== 1 ? suffix : ""}`;

export const pluralizeWord = (count: number, noun: string, suffix = "s") =>
  `${noun}${count !== 1 ? suffix : ""}`;

export type SerializedJSON = string & { __brand: "SerializedJSON" };

/**
 * Join a list of elements with comma seperators except the last element
 * which is joined with "and".
 * @param items
 */
export const sentanceList = (items: ReactNode[]): (ReactNode | string)[] =>
  items.flatMap((item, i) => {
    return [
      item,
      (() => {
        if (i === items.length - 2) {
          return " and ";
        }

        return i === items.length - 1 ? "" : ", ";
      })(),
    ];
  });

export const isJsonObject = (s: unknown): s is SerializedJSON => {
  try {
    const parsed = JSON.parse(s as string);
    //JSON.parse also reads "true" "false" and "null" as valid jsons, which they aren't.
    //Therefore these additional checks are necessarry.
    return typeof parsed === "object" && parsed !== null;
  } catch {
    return false;
  }
};

type TaktileError = {
  type: string;
  loc: string[];
  msg: string;
  input: string;
};

const isTaktileError = (err: unknown): err is TaktileError => {
  return (
    typeof err === "object" && err !== null && "type" in err && "msg" in err
  );
};

/**
 * Safely extracts the first error detail from an Axios error response.
 * Returns the error detail if available, otherwise returns a fallback message.
 */
export const getApiErrorDetail = (
  err: unknown,
  fallback: string = "An error occurred",
): string => {
  if (err instanceof AxiosError && err?.response?.data?.errors) {
    const errors = err.response.data.errors;
    if (Array.isArray(errors) && errors.length > 0 && errors[0]?.detail) {
      return errors[0].detail;
    }
  }
  return fallback;
};

export const errorMessage = (err: unknown): string => {
  if (Array.isArray(err)) {
    return err.map((item) => errorMessage(item)).join(", ");
  }

  if (err instanceof AxiosError && err?.response?.data?.detail) {
    const detail = err.response.data.detail;
    if (Array.isArray(detail)) {
      return detail.map((item) => item.msg).join(", ");
    } else {
      return err.response.data.detail;
    }
  }

  if (isTaktileError(err)) {
    return err.msg;
  } else if (err instanceof Error) {
    return err.message;
  } else if (typeof err === "string") {
    return err;
  } else if (typeof err === "object") {
    return JSON.stringify(err);
  } else if (err === undefined || err === null) {
    return "";
  }
  return String(err);
};

export const compareStringsAscendingCaseInsensitive = (
  a: string,
  b: string,
) => {
  a = a.toLowerCase();
  b = b.toLowerCase();
  if (a < b) {
    return -1;
  }
  if (a > b) {
    return 1;
  }
  return 0;
};

export const getMaximumDisplayedCount = (
  count: number | undefined,
  maxCount: number,
) => {
  if (count === undefined) {
    return count;
  }
  return count > maxCount ? `${maxCount}+` : count;
};

export const looksLikeJson = (value: string | undefined): boolean => {
  if (!value) return false;

  const trimmed = value.trim();

  return (
    (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
    (trimmed.startsWith("[") && trimmed.endsWith("]"))
  );
};

export const normalizeToSortedString = (value: string | string[]): string => {
  if (Array.isArray(value)) {
    // Sort array for order-independent comparison
    return JSON.stringify([...value].sort());
  }
  return value;
};

export const isProbablyMarkdown = (value: JSONValue): value is string => {
  if (!value || typeof value !== "string") return false;

  if (isEmail(value)) return false;
  if (isValidURL(value)) return false;

  // If this is multiline string, we consider it as markdown
  // and will try to render as markdown
  return value.includes("\n");
};

export const isEmail = (value: string) => {
  // Limit input length first
  if (value.length > 256) return false;
  // Use a more deterministic regex with atomic groups to prevent backtracking
  const emailRegex = /^[^@\s]{1,64}@[^@\s.]{1,253}\.[^@\s.]{2,63}$/;
  return emailRegex.test(value);
};

export const isValidURL = (url: string) => {
  return isValidHTTPURL(url.startsWith("www.") ? `https://${url}` : url);
};

// djb2 — deterministic 32-bit unsigned hash.
export const hashString = (s: string): number =>
  Array.from(s).reduce((h, c) => ((h << 5) + h + c.charCodeAt(0)) | 0, 5381) >>>
  0;

export const coerceToString = (v: unknown): string => {
  if (v === null || v === undefined) return "";
  if (typeof v === "string") return v;
  return String(v);
};
